
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { Box, Grid, Tooltip, } from "@mui/material";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { DateFilterOperator, NumberFilterOperator, StatusFilterOperator, TaskProblemFilterOperator, TextFilterOperator } from "components/shared/filters";

import moment from "moment";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { SetPreferenceSignature } from "hooks/preferences";
import { SC_Constants } from "utils/sc-constants";
import { TeamTab } from "models/teams";
import ContainsOnlyTextFilterOperator from "components/shared/filters/ContainsOnlyTextFilterOperator";
import ParentWorkItemListTooltip from "components/teams/grid/ParentWorkItemListTooltip";
import useTeamPermissions from "hooks/teams/useTeamPermissions";
import { useFeatureEnabled } from "hooks/shared";

type UseTeamColumnsProps = {
  tab: TeamTab,
  settings?: IDashboardSettings | null,
  setSettings?: SetPreferenceSignature,
};

type UseTeamColumnsResult = [
  GridColDef[],
  GridColumnVisibilityModel,
  ColumnVisibilityOrOrderChangeHandler,
];

type UseTeamColumnsSignature = (props: UseTeamColumnsProps) => UseTeamColumnsResult;

const useTeamColumns: UseTeamColumnsSignature = (props) => {
  const { tab } = props;
  const { settings, setSettings } = props;
  const permissions = useTeamPermissions();
  const columnBase: GridColDef[] = [
    {
      field: 'teamName', headerName: 'Team', flex: 1, minWidth: 300, sortable: true, resizable: true, hideable: false, hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderHeader: (params) => {
        return (
          <Tooltip title="Team" arrow>
            <div>{"Team"}</div>
          </Tooltip>
        )
      }
    },
    {
      field: 'workItemId', headerName: 'WI ID', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, hideSortIcons: false, disableReorder: true,
      filterOperators: NumberFilterOperator,
      renderHeader: (params) => {
        return (
          <Tooltip title="Work Item ID" arrow>
            <div>{"WI ID"}</div>
          </Tooltip>
        )
      }
    },
    {
      field: 'taskName', headerName: 'Task', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const taskId = params.row.taskId;
        const hasAccess = params.row.userHasAccess;
        return (
          <Tooltip title={name} arrow>
            <Grid container alignItems="center" style={{ whiteSpace: 'break-spaces', lineHeight: '1.5em', height: '3em'}}>
              <Grid item >
              {hasAccess && 
              <a onClick={() => navigateToTask(taskId)} style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} rel="noopener noreferrer">
                {name}
              </a>
              || name}
              </Grid>
            </Grid>
          </Tooltip>
        );
      }
    },
    {
      field: 'workItemName', headerName: 'Work Item', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: (tab != 'work-items'), headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const workItemId = params.row.workItemId;
        return (
          <Tooltip title={name} arrow>
            <div>
              <a href={navigateToWorkItem(workItemId)} style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} rel="noopener noreferrer">
                {name}
              </a>
            </div>
          </Tooltip>
        );
      }
    },
    { field: 'workItemTemplateName', headerName: 'Work Item Template', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    { field: 'workspaceName', headerName: 'Workspace', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    {
      field: 'startDate', headerName: 'Start Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const startDate = params.value;
        return (
          <div>
            {formatDate(startDate)}
          </div>
        );
      }
    },
    {
      field: 'endDate', headerName: 'End Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const endDate = params.value;
        return (
          <div>
            {formatDate(endDate)}
          </div>
        );
      }
    },
    { field: 'workItemStatus', headerName: 'Status', minWidth: 120, width: 200, sortable: true, resizable: true,  headerClassName: 'header', hideSortIcons: false, filterOperators: StatusFilterOperator },
    { field: 'taskStatusName', headerName: 'Status', minWidth: 120, width: 200, sortable: true, resizable: true,  headerClassName: 'header', hideSortIcons: false, filterOperators: StatusFilterOperator },
    {
      field: 'memberName', headerName: 'Assignee', headerClassName: 'header', sortable: true, hideSortIcons: false, resizable: true, width: 200, filterOperators: TextFilterOperator
    },
    { field: 'contentTypeName', headerName: 'Content Type', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    {
      field: 'parentWorkItems', headerName: 'Parent Work Item', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: ContainsOnlyTextFilterOperator,
      renderCell: (params) => {
        const parents = params.value;
         return (
          <Box >
            {
              parents?.length > 0 && <ParentWorkItemListTooltip canAccessAllWorkItems={permissions?.canAccessAllWorkItems} parent={parents ? parents : ''} />
            }
          </Box>
        );
      }
    },
    {
      field: 'isProblem', headerName: 'Problem', headerClassName: 'header', sortable: true, hideSortIcons: false, resizable: true, minWidth: 120, width: 200, filterOperators: TaskProblemFilterOperator,
      renderCell: (params) => {
        return (params.value ? "Yes" : "")
      }
    },
    {
      field: 'isLate', headerName: 'Late', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TaskProblemFilterOperator,
      renderCell: (params) => {
        return (params.value ? "Yes" : "")
      }
    },

    {
      field: 'isMsBreached', headerName: 'Milestone', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TaskProblemFilterOperator,
      renderCell: (params) => {
        return (params.value ? "Yes" : "")
      }
    },
    {
      field: 'isLDBreached', headerName: 'LD Breached', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TaskProblemFilterOperator,
      renderHeader: (params) => {
        return (
          <Tooltip title="Locked Date Breached" arrow>
            <div>{"LD Breached"}</div>
          </Tooltip>
        )
      },
      renderCell: (params) => {
        return (params.value ? "Yes" : "")
      }
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const [columns, setColumns] = useState<GridColDef[]>(columnBase);
  const [ wave4Enabled ] = useFeatureEnabled('wave-4');
  
  const navigateToWorkItem = (workItemId: number) => {
    localStorage.setItem(SC_Constants.projectProgress, tab);
    const allSegment = 'my';
    return wave4Enabled 
      ? `${process.env.REACT_APP_PUBLIC_URL}` + `/workitems/${workItemId}/overview`
      : `${process.env.REACT_APP_CLASSIC_URL}` + `/workitemenduser/editworkitem?workitemId=${workItemId}`;
  };

  const navigateToTask = (taskId: number) => {
    navigate(`/tasks/${taskId}`, { state: { from: location }});
  };

  const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);

    if (tab == 'tasks') {
      const workspaceIdx = newColumns.findIndex(c => c.field === 'workspaceName');
      if (workspaceIdx > -1) {
        newColumns.splice(workspaceIdx, 1);
      }

      const workItemStatusIdx = newColumns.findIndex(c => c.field === 'workItemStatus');
      if (workItemStatusIdx > -1) {
        newColumns.splice(workItemStatusIdx, 1);
      }

      const ldBreachedIdx = newColumns.findIndex(c => c.field === 'isLDBreached');
      if (ldBreachedIdx > -1) {
        newColumns.splice(ldBreachedIdx, 1);
      }
    }

    if (tab == 'work-items') {
      const workItemIdx = newColumns.findIndex(c => c.field === 'workItemId');
      if (workItemIdx > -1) {
        newColumns.splice(workItemIdx, 1);
      }

      const taskNameIdx = newColumns.findIndex(c => c.field === 'taskName');
      if (taskNameIdx > -1) {
        newColumns.splice(taskNameIdx, 1);
      }

      const taskStatusIdx = newColumns.findIndex(c => c.field === 'taskStatusName');
      if (taskStatusIdx > -1) {
        newColumns.splice(taskStatusIdx, 1);
      }

      const memberNameIdx = newColumns.findIndex(c => c.field === 'memberName');
      if (memberNameIdx > -1) {
        newColumns.splice(memberNameIdx, 1);
      }

      const contentTypeIdx = newColumns.findIndex(c => c.field === 'contentTypeName');
      if (contentTypeIdx > -1) {
        newColumns.splice(contentTypeIdx, 1);
      }

      const parentWorkItemsIdx = newColumns.findIndex(c => c.field === 'parentWorkItems');
      if (parentWorkItemsIdx > -1) {
        newColumns.splice(parentWorkItemsIdx, 1);
      }

      const isProblemIdx = newColumns.findIndex(c => c.field === 'isProblem');
      if (isProblemIdx > -1) {
        newColumns.splice(isProblemIdx, 1);
      }

      const isLateIdx = newColumns.findIndex(c => c.field === 'isLate');
      if (isLateIdx > -1) {
        newColumns.splice(isLateIdx, 1);
      }
    }

    setColumns(newColumns);
  }, [tab, settings]);

  return [
    columnsModel,
    visibilityModel,
    handleColumnVisibilityOrOrderChange
  ];
};

export default useTeamColumns;